import { useState } from 'react';
import { Button } from '@lms/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, updateApiClientToken } from '../lib/api-client';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';
import { useAuthStore } from '../store/auth';
import { ProfileDialog } from './ProfileDialog';
import { invalidateProfileQueries } from '../lib/profile-cache';

interface ConfirmEmailChangeModalProps {
  token: string;
  onClose: () => void;
  onConfirmed: (oldEmail: string, newEmail: string) => void;
}

export function ConfirmEmailChangeModal({
  token,
  onClose,
  onConfirmed,
}: ConfirmEmailChangeModalProps) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const confirm = useMutation({
    gcTime: 0,
    mutationFn: async () => {
      const initiatingSession = useAuthStore.getState();
      if (!initiatingSession.token || !initiatingSession.user)
        throw new Error(t(copy.profile.emailConfirmError));
      const result = await apiClient.auth.confirmEmailChange({ token });
      return {
        result,
        initiatingToken: initiatingSession.token,
        initiatingUserId: initiatingSession.user.id,
      };
    },
    onSuccess: ({ result, initiatingToken, initiatingUserId }) => {
      const current = useAuthStore.getState();
      if (
        current.token !== initiatingToken ||
        current.user?.id !== initiatingUserId ||
        result.user.id !== initiatingUserId
      )
        return;
      const oldEmail = current.user.email;
      updateApiClientToken(result.accessToken);
      current.setAuth(result.accessToken, result.user);
      invalidateProfileQueries(queryClient, result.user.id);
      onConfirmed(oldEmail, result.user.email);
      onClose();
    },
    onError: () => setError(t(copy.profile.emailConfirmError)),
  });
  return (
    <ProfileDialog
      title={t(copy.profile.emailConfirmTitle)}
      description={t(copy.profile.emailConfirmDescription)}
      busy={confirm.isPending}
      onClose={onClose}
    >
      <div className="overflow-y-auto px-6 pb-6" aria-busy={confirm.isPending}>
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-error-container p-4 text-sm text-on-error-container"
          >
            {error}
          </p>
        )}
      </div>
      <footer className="flex shrink-0 flex-col-reverse gap-3 bg-surface-container-low px-6 py-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onClose} disabled={confirm.isPending}>
          {t(copy.common.cancel)}
        </Button>
        <Button
          type="button"
          onClick={() => {
            setError('');
            confirm.mutate();
          }}
          disabled={confirm.isPending}
          className="bg-gradient-to-r from-primary to-primary-container"
        >
          {t(confirm.isPending ? copy.profile.saving : copy.profile.emailConfirm)}
        </Button>
      </footer>
    </ProfileDialog>
  );
}
